import { useEffect, useCallback, useState, useMemo } from 'react';
import useDebounce from '../../../../lib/useDebounce';
import { usePersistentClipStateAndActions } from './common';
import { useEventsContext } from '../../../../context/events/EventsContext';
import useSRAnnouncer from '../../../../lib/useSRAnnouncer';

const MIN_SEARCH_LIMIT = 3;

export function sanitizeSearchTerm(searchTerm: string): string {
  return searchTerm.replace(/[^-.0-9A-Za-z\u00C0-\u00ff]/gi, '');
}

/**
 * @description Handles search of a clip
 */
const useClipsSearch = () => {
  const DELAY = 500;
  const { setSearch } = usePersistentClipStateAndActions();
  const [searchInput, setSearchInput] = useState<string | null>(null);
  const [eventTracking] = useEventsContext();
  const debouncedSearchInput = useDebounce(searchInput, DELAY);
  const { announce } = useSRAnnouncer();

  const searchError = useMemo(() => {
    // if search is in empty state
    if (searchInput === null || searchInput === '') {
      return null;
    }
    const sanitizedTerm = sanitizeSearchTerm(searchInput);
    // if sanitized search hits minimum threshold
    if (sanitizedTerm.length >= MIN_SEARCH_LIMIT) {
      return null;
    }
    // if the search hits minimum threshold and the sanitized
    // search meets minumum threshold
    const searchDoesNotMeetMinimumValidChars =
      searchInput.length >= MIN_SEARCH_LIMIT &&
      sanitizedTerm.length <= MIN_SEARCH_LIMIT;

    if (searchDoesNotMeetMinimumValidChars) {
      // TODO: verify search error message
      return 'Try searching by plain letters, words, or numbers';
    }
    return null;
  }, [searchInput]);

  useEffect(() => {
    // explict check for null to avoid initial query
    if (debouncedSearchInput === null) {
      return;
    }
    if (!searchError) {
      // if the search is less than 3 characters we still want to persist
      // the query handles the logic to decide whether or not to be called
      setSearch(debouncedSearchInput);
      if (debouncedSearchInput && debouncedSearchInput !== '')
        announce(`Searching for ${debouncedSearchInput}`);
    }
  }, [announce, debouncedSearchInput, searchError, setSearch]);

  const onBlur = useCallback(
    ({ projectId }: { projectId: string }) => {
      // avoid tracking blur on initial state
      if (debouncedSearchInput) {
        eventTracking.trackClipSearch({ projectId });
      }
    },
    [eventTracking, debouncedSearchInput]
  );

  return {
    searchInput,
    searchError,
    setSearchInput,
    onBlur,
  };
};

export default useClipsSearch;
